import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Receipt } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

export function ExpensesPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('expenses');

  return (
    <>
      <PageHeader
        title={t('expenses.title')}
        action={
          // The categories tab has its own create button on its card.
          tab === 'expenses' && can(P.FINANCE_EXPENSES_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/expenses/new')}>
              {t('expenses.new')}
            </Button>
          )
        }
      />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'expenses', label: t('expenses.title') },
          { id: 'categories', label: t('expenses.categories') },
        ]}
      />
      <div className="mt-5">{tab === 'expenses' ? <ExpenseList /> : <CategoryList />}</div>
    </>
  );
}

function ExpenseList() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['expenses', page, filters],
    queryFn: () => api.get<Paginated<any> & { totalAmount: number }>('/expenses', { page, pageSize: 25, ...filters }),
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get<any[]>('/expense-categories'),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) => api.post(`/expenses/${id}/${action}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['expenses'] }),
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <div className="mb-4">
        <Metric label={t('common.total')} value={formatMoney(data?.totalAmount ?? 0, 'EGP', locale)} />
      </div>

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
          />
          <Select value={filters.categoryId ?? ''} onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}>
            <option value="">{t('expenses.categories')} — {t('common.all')}</option>
            {categories?.map((c) => (
              <option key={c.id} value={c.id}>{localized(c)}</option>
            ))}
          </Select>
          <Select value={filters.status ?? ''} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {['DRAFT', 'APPROVED', 'PAID', 'CANCELLED'].map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
          <Input type="date" value={filters.from ?? ''} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          <Input type="date" value={filters.to ?? ''} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Receipt className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.date')}</Th>
                  <Th>{t('common.description')}</Th>
                  <Th>{t('expenses.categories')}</Th>
                  <Th>{t('expenses.vendor')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('common.amount')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((expense) => (
                  <Tr key={expense.id}>
                    <Td className="tabular">{formatDate(expense.expenseDate, locale)}</Td>
                    <Td className="max-w-xs">
                      <span className="block truncate">{expense.description}</span>
                      {expense.sourceType === 'PAYROLL_PERIOD' && (
                        <Badge tone="plasma" className="mt-0.5">auto · payroll</Badge>
                      )}
                    </Td>
                    <Td className="text-muted">{localized(expense.category)}</Td>
                    <Td className="text-muted">{expense.vendorName ?? '—'}</Td>
                    <Td><Badge tone={statusTone(expense.status)}>{enumLabel(expense.status, locale)}</Badge></Td>
                    <Td align="end" className="tabular">{formatMoney(expense.amount, expense.currency, locale)}</Td>
                    <Td align="end">
                      {can(P.FINANCE_EXPENSES_MANAGE) && expense.status === 'DRAFT' && (
                        <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: expense.id, action: 'approve' })}>
                          {t('common.approve')}
                        </Button>
                      )}
                      {can(P.FINANCE_EXPENSES_MANAGE) && expense.status === 'APPROVED' && (
                        <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: expense.id, action: 'mark-paid' })}>
                          {t('expenses.markPaid')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

function CategoryList() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['expense-categories', 'all'],
    queryFn: () => api.get<any[]>('/expense-categories', { includeInactive: 'true' }),
  });

  if (isLoading) return <LoadingBlock />;

  return (
    <>
      <Card padded={false}>
        <CardHeader
          title={t('expenses.categories')}
          className="p-5 pb-0"
          action={
            can(P.FINANCE_EXPENSES_MANAGE) && (
              <Button
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/expenses/categories/new')}
              >
                {t('common.create')}
              </Button>
            )
          }
        />
        <Table>
          <thead>
            <tr>
              <Th>{t('common.code')}</Th>
              <Th>{t('common.name')}</Th>
              <Th>{t('common.status')}</Th>
              <Th align="end">{t('expenses.title')}</Th>
            </tr>
          </thead>
          <tbody>
            {data?.map((c) => (
              <Tr key={c.id}>
                <Td className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{c.code}</Td>
                <Td>{localized(c)}</Td>
                <Td>
                  <Badge tone={c.isActive ? 'success' : 'neutral'}>
                    {c.isActive ? t('employees.active') : t('employees.inactive')}
                  </Badge>
                </Td>
                <Td align="end" className="tabular">{c._count.expenses}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Card>

    </>
  );
}
